import {
    LIST_FINANCIAL_YEARS_URL,
    LIST_SETTLEMENT_OPENING_BALANCES_URL,
    SET_SETTLEMENT_OPENING_BALANCE_URL,
    CLEAR_SETTLEMENT_OPENING_BALANCE_URL,
    SETTLEMENT_WORKFLOW_DATA_URL,
    CREATE_SETTLEMENT_URL,
    UPDATE_SETTLEMENT_URL,
    LIST_SETTLEMENTS_URL,
    GET_SETTLEMENT_URL,
    APPROVE_SETTLEMENT_URL,
    CANCEL_SETTLEMENT_URL,
    DELETE_SETTLEMENT_URL,
    RECORD_SETTLEMENT_PAYMENT_URL,
    MODIFY_SETTLEMENT_PAYMENT_URL,
    ALLOCATE_SETTLEMENT_ADVANCE_URL,
    UNALLOCATE_SETTLEMENT_PAYMENT_URL,
    LIST_SETTLEMENT_PAYMENTS_URL,
    LIST_UNALLOCATED_ADVANCES_URL,
    GET_ADVANCE_BALANCE_URL,
    FETCH_OPERATOR_DOCUMENTS_URL,
    FETCH_OPERATOR_BILLING_URL
} from "../utils/api-url";
import { postCall } from "../utils/network.util";

const handleResponse = (data, onSuccess, onFailure) => {
    if (data && data.status === "SUCCESS") {
        const clientPayload = data.payload;
        if (clientPayload && clientPayload.Status === "SUCCESS") {
            onSuccess(clientPayload.Payload, clientPayload.Message);
            return;
        }
        onFailure(clientPayload && clientPayload.Message ? clientPayload.Message : "Request failed");
        return;
    }
    onFailure("Request failed");
};

// All settlement endpoints take agentId — server resolves it to actor + privilege
// + operator scoping. The client no longer supplies actor identity directly.
const withAuth = (body) => Object.assign({}, body, { agentId: global.agentId });

export const listFinancialYears = (onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(LIST_FINANCIAL_YEARS_URL, withAuth({}))
        .then((data) => {
            handleResponse(
                data,
                (payload) => onSuccess(payload && payload.financialYears ? payload.financialYears : []),
                onFailure
            );
        })
        .catch((reason) => {
            console.error("listFinancialYears failed", reason);
            onFailure("Failed to load financial years");
        });
};

export const listSettlementOpeningBalances = ({ financialYear }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(LIST_SETTLEMENT_OPENING_BALANCES_URL, withAuth({ financialYear }))
        .then((data) => {
            handleResponse(
                data,
                (payload) => onSuccess(payload && payload.openingBalances ? payload.openingBalances : []),
                onFailure
            );
        })
        .catch((reason) => {
            console.error("listSettlementOpeningBalances failed", reason);
            onFailure("Failed to load opening balances");
        });
};

export const setSettlementOpeningBalance = ({ operatorCode, financialYear, amount, remarks }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(SET_SETTLEMENT_OPENING_BALANCE_URL, withAuth({ operatorCode, financialYear, amount, remarks }))
        .then((data) => {
            handleResponse(data, (payload, msg) => onSuccess(payload, msg), onFailure);
        })
        .catch((reason) => {
            console.error("setSettlementOpeningBalance failed", reason);
            onFailure("Failed to save opening balance");
        });
};

export const clearSettlementOpeningBalance = ({ operatorCode, financialYear }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(CLEAR_SETTLEMENT_OPENING_BALANCE_URL, withAuth({ operatorCode, financialYear }))
        .then((data) => {
            handleResponse(data, (payload, msg) => onSuccess(payload, msg), onFailure);
        })
        .catch((reason) => {
            console.error("clearSettlementOpeningBalance failed", reason);
            onFailure("Failed to clear opening balance");
        });
};

/**
 * "Settlement extras" — FY context + opening balances + prior outstandings
 * for the client-filtered operator set. Activity totals come from the
 * travels server (getWorkFlowAction + getActiveOperatorsWithConfigs).
 *
 * input.operatorCodes: client-filtered list of operator codes to look up priors for.
 */
export const fetchSettlementExtras = ({ periodStart, periodEnd, operatorCodes }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(SETTLEMENT_WORKFLOW_DATA_URL, withAuth({ periodStart, periodEnd, operatorCodes }))
        .then((data) => {
            handleResponse(data, (payload) => onSuccess(payload), onFailure);
        })
        .catch((reason) => {
            console.error("fetchSettlementExtras failed", reason);
            onFailure("Failed to load settlement extras");
        });
};

export const createSettlement = (payload, onSuccess = () => {}, onFailure = () => {}) => {
    // Server attaches its own actor from agentId; client-supplied actor is ignored.
    return postCall(CREATE_SETTLEMENT_URL, withAuth({ payload }))
        .then((data) => {
            handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure);
        })
        .catch((reason) => {
            console.error("createSettlement failed", reason);
            onFailure("Failed to create settlement");
        });
};

/**
 * Update an existing DRAFT settlement. Server rejects any non-DRAFT row.
 * payload shape: { id, openingBalance, subTotalCharges, subTotalCredits,
 *   gstAmount, invoiceAmount, netSettlement, isOverridden, lines }
 */
export const updateSettlement = (payload, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(UPDATE_SETTLEMENT_URL, withAuth({ payload }))
        .then((data) => {
            handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure);
        })
        .catch((reason) => {
            console.error("updateSettlement failed", reason);
            onFailure("Failed to update settlement");
        });
};

export const listSettlements = (filters, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(LIST_SETTLEMENTS_URL, withAuth({ filters: filters || {} }))
        .then((data) => {
            handleResponse(
                data,
                (payload) => onSuccess(payload && payload.settlements ? payload.settlements : []),
                onFailure
            );
        })
        .catch((reason) => {
            console.error("listSettlements failed", reason);
            onFailure("Failed to load settlements");
        });
};

export const getSettlement = ({ id }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(GET_SETTLEMENT_URL, withAuth({ id }))
        .then((data) => {
            handleResponse(data, (payload) => onSuccess(payload), onFailure);
        })
        .catch((reason) => {
            console.error("getSettlement failed", reason);
            onFailure("Failed to load settlement");
        });
};

export const approveSettlement = ({ id, comments }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(APPROVE_SETTLEMENT_URL, withAuth({ id, comments }))
        .then((data) => {
            handleResponse(data, (payload, msg) => onSuccess(payload, msg), onFailure);
        })
        .catch((reason) => {
            console.error("approveSettlement failed", reason);
            onFailure("Failed to approve settlement");
        });
};

export const cancelSettlement = ({ id, comments }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(CANCEL_SETTLEMENT_URL, withAuth({ id, comments }))
        .then((data) => {
            handleResponse(data, (payload, msg) => onSuccess(payload, msg), onFailure);
        })
        .catch((reason) => {
            console.error("cancelSettlement failed", reason);
            onFailure("Failed to cancel settlement");
        });
};

/**
 * Soft-delete a settlement (is_active = 0). Server restricts this to
 * DRAFT or CANCELLED statuses; finalized invoices must be cancelled first.
 */
export const deleteSettlement = ({ id, reason }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(DELETE_SETTLEMENT_URL, withAuth({ id, reason }))
        .then((data) => {
            handleResponse(data, (payload, msg) => onSuccess(payload, msg), onFailure);
        })
        .catch((reason) => {
            console.error("deleteSettlement failed", reason);
            onFailure("Failed to delete settlement");
        });
};

// ---- payments + advances ----

/**
 * Record a payment. settlementId is OPTIONAL:
 *   - If provided -> allocated payment against that settlement
 *   - If omitted  -> unallocated advance for the operator
 *
 * payload: { settlementId?, operatorCode, direction, amount, paymentMethod,
 *            paymentReference?, paymentDate, remarks? }
 * paymentMethod is one of: CASH | UPI | NET_BANKING | NEFT | CHEQUE
 * direction is INBOUND (received from operator) | OUTBOUND (paid to operator)
 */
export const recordSettlementPayment = (payload, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(RECORD_SETTLEMENT_PAYMENT_URL, withAuth(payload))
        .then((data) => handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure))
        .catch((reason) => {
            console.error("recordSettlementPayment failed", reason);
            onFailure("Failed to record payment");
        });
};

/**
 * Modify a payment. Two shapes:
 *   - { paymentId, void: true, reason }   -> soft-void
 *   - { paymentId, patch: { amount?, paymentMethod?, paymentReference?, paymentDate?, remarks? } }
 *
 * Recomputes parent settlement totals if the payment was allocated.
 */
export const modifySettlementPayment = (input, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(MODIFY_SETTLEMENT_PAYMENT_URL, withAuth(input))
        .then((data) => handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure))
        .catch((reason) => {
            console.error("modifySettlementPayment failed", reason);
            onFailure("Failed to modify payment");
        });
};

export const allocateSettlementAdvance = ({ paymentId, settlementId }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(ALLOCATE_SETTLEMENT_ADVANCE_URL, withAuth({ paymentId, settlementId }))
        .then((data) => handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure))
        .catch((reason) => {
            console.error("allocateSettlementAdvance failed", reason);
            onFailure("Failed to allocate advance");
        });
};

export const unallocateSettlementPayment = ({ paymentId }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(UNALLOCATE_SETTLEMENT_PAYMENT_URL, withAuth({ paymentId }))
        .then((data) => handleResponse(data, (resp, msg) => onSuccess(resp, msg), onFailure))
        .catch((reason) => {
            console.error("unallocateSettlementPayment failed", reason);
            onFailure("Failed to unallocate payment");
        });
};

export const listSettlementPayments = ({ settlementId }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(LIST_SETTLEMENT_PAYMENTS_URL, withAuth({ settlementId }))
        .then((data) => handleResponse(
            data,
            (payload) => onSuccess(payload && payload.payments ? payload.payments : []),
            onFailure
        ))
        .catch((reason) => {
            console.error("listSettlementPayments failed", reason);
            onFailure("Failed to load payments");
        });
};

export const listUnallocatedAdvances = ({ operatorCode }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(LIST_UNALLOCATED_ADVANCES_URL, withAuth({ operatorCode }))
        .then((data) => handleResponse(
            data,
            (payload) => onSuccess(payload && payload.advances ? payload.advances : []),
            onFailure
        ))
        .catch((reason) => {
            console.error("listUnallocatedAdvances failed", reason);
            onFailure("Failed to load advances");
        });
};

export const getAdvanceBalance = ({ operatorCode }, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(GET_ADVANCE_BALANCE_URL, withAuth({ operatorCode }))
        .then((data) => handleResponse(data, (resp) => onSuccess(resp), onFailure))
        .catch((reason) => {
            console.error("getAdvanceBalance failed", reason);
            onFailure("Failed to load advance balance");
        });
};

// Fetch the operator's portal billing documents (admin proxies to Console).
export const fetchOperatorDocuments = ({ operatorCode, from, to, kinds } = {}, onSuccess = () => {}, onFailure = () => {}) => {
    const kindsParam = Array.isArray(kinds) ? kinds.join(",") : (kinds || undefined);
    return postCall(FETCH_OPERATOR_DOCUMENTS_URL, withAuth({ operatorCode, from, to, kinds: kindsParam }))
        .then((data) => {
            handleResponse(data, (payload) => onSuccess(payload), onFailure);
        })
        .catch((reason) => {
            console.error("fetchOperatorDocuments failed", reason);
            onFailure("Failed to fetch billing documents");
        });
};

// Fetch the operator's live billing position (admin proxies to Console).
export const fetchOperatorBilling = ({ operatorCode } = {}, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(FETCH_OPERATOR_BILLING_URL, withAuth({ operatorCode }))
        .then((data) => {
            handleResponse(data, (payload) => onSuccess(payload), onFailure);
        })
        .catch((reason) => {
            console.error("fetchOperatorBilling failed", reason);
            onFailure("Failed to fetch billing position");
        });
};
